// sleeperAdapter.js
// ---------------------------------------------------------------------------
// Turns a Sleeper league_id into render-ready board rows.
//
// This is the reusable engine core: a *source adapter*. It knows about
// Sleeper's endpoints and ID systems and nothing about the renderer. The
// renderer is a pure function over the { week, rows, standings } it emits.
//
// Design notes:
//  - Pure + testable: fetchLeague() does I/O; everything else is pure
//    transforms you can unit-test against a fixture (see selfTest at bottom).
//  - Two ID systems joined here (matchups.roster_id -> rosters.owner_id ->
//    users.user_id); nothing downstream needs to know they existed.
//  - Status is Tier 1 (week-close proxy) but deriveStatus() already takes the
//    poll-history + now it would need for Tier 2, so upgrading is one function.
// ---------------------------------------------------------------------------

import {
  annotatePlayoffRows,
  championshipWeek,
  championDecided,
  fetchBracket,
  finalPlacements,
  isPlayoffWeek,
  lastPlaceRosterId,
  playoffWeekStart,
} from './playoffs';

const BASE = 'https://api.sleeper.app/v1';
const CDN_THUMB = 'https://sleepercdn.com/avatars/thumbs';
const CDN_FULL = 'https://sleepercdn.com/avatars';

// ---- low-level fetch (the only I/O in this file) --------------------------

async function getJSON(url, fetchImpl = fetch) {
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`Sleeper ${res.status} for ${url}`);
  return res.json();
}

// Fetch the current NFL week. In-season this is a live week; off-season it's 0.
export async function fetchNflState(fetchImpl = fetch) {
  return getJSON(`${BASE}/state/nfl`, fetchImpl);
}

// Fetch the league endpoints needed for a given week, in parallel.
// Pass an explicit `week` to pin a historical week (e.g. testing off-season).
//
// The winners bracket is a FIFTH call, but only during playoff weeks -- we
// don't know whether it's a playoff week until the league object lands, so
// the bracket fetch is chained after the first four rather than folded into
// them. In-season that costs one extra round trip on ~3 weeks of the year,
// behind the same 45s edge cache as everything else.
export async function fetchLeague(leagueId, week, fetchImpl = fetch, opts = {}) {
  const [league, users, rosters, matchups] = await Promise.all([
    getJSON(`${BASE}/league/${leagueId}`, fetchImpl),
    getJSON(`${BASE}/league/${leagueId}/users`, fetchImpl),
    getJSON(`${BASE}/league/${leagueId}/rosters`, fetchImpl),
    getJSON(`${BASE}/league/${leagueId}/matchups/${week}`, fetchImpl),
  ]);

  // opts.bracket lets a caller that already fetched it (resolveDisplayWeek
  // does, off-season) pass it in and skip the second trip.
  let bracket = opts.bracket ?? null;
  if (bracket == null && isPlayoffWeek(week, league)) {
    bracket = await fetchBracket(leagueId, fetchImpl, BASE);
  }

  return { league, users, rosters, matchups, week, bracket: bracket || [] };
}

// ---- pure transforms ------------------------------------------------------

// Sleeper stores points as int + decimal parts (fpts:1470, fpts_decimal:38 ->
// 1470.38). Weekly matchup points are already floats. This normalizes both.
function combineFpts(whole, decimal) {
  const w = whole ?? 0;
  const d = decimal ?? 0;
  return Number(`${w}.${d}`);
}

export function buildUsersById(users) {
  const map = {};
  for (const u of users) {
    const meta = u.metadata || {};
    map[u.user_id] = {
      userId: u.user_id,
      name: meta.team_name || u.display_name, // team_name is optional per user
      displayName: u.display_name,
      // avatar: league-uploaded (meta.avatar, a full URL) wins; else the
      // user's global avatar hash served from the CDN; else null.
      avatar: meta.avatar || (u.avatar ? `${CDN_THUMB}/${u.avatar}` : null),
      avatarFull: meta.avatar || (u.avatar ? `${CDN_FULL}/${u.avatar}` : null),
    };
  }
  return map;
}

export function buildRosterById(rosters) {
  const map = {};
  for (const r of rosters) {
    const s = r.settings || {};
    map[r.roster_id] = {
      rosterId: r.roster_id,
      ownerId: r.owner_id,
      wins: s.wins ?? 0,
      losses: s.losses ?? 0,
      ties: s.ties ?? 0,
      fpts: combineFpts(s.fpts, s.fpts_decimal),
      fptsAgainst: combineFpts(s.fpts_against, s.fpts_against_decimal),
    };
  }
  return map;
}

// Rank = wins desc, then season points-for desc, then roster_id asc.
//
// The final roster_id tiebreak is NOT cosmetic. Without it, two teams with
// identical wins and identical fpts have no defined order, so Array.sort can
// return them either way between polls -- and a rank cell that swaps 4<->5
// every 45 seconds is exactly the "random flipping" a Solari board must never
// do. A stable, arbitrary-but-fixed tiebreak costs nothing and makes rank a
// pure function of the data.
//
// fpts is SEASON points-for. If Sleeper folds an in-progress week's points
// into it (unverified -- see handoff §6), ranks would drift on every poll
// mid-week. `rankBasis` lets a caller pin ranks to completed results only;
// the default preserves existing behavior.
export function computeRanks(rosterById, { rankBasis = 'fpts' } = {}) {
  const rows = Object.values(rosterById);
  const pf = (r) => (rankBasis === 'wins-only' ? 0 : r.fpts);
  rows.sort((a, b) =>
    (b.wins - a.wins) ||
    (pf(b) - pf(a)) ||
    (a.rosterId - b.rosterId)   // stable final tiebreak; see note above
  );
  const rankByRoster = {};
  rows.forEach((r, i) => { rankByRoster[r.rosterId] = i + 1; });
  return rankByRoster;
}

// Decorate one matchup entry into a render-ready team object.
function decorateTeam(entry, rosterById, usersById, rankByRoster) {
  const roster = rosterById[entry.roster_id] || {};
  const user = usersById[roster.ownerId] || {};
  return {
    rosterId: entry.roster_id,
    name: user.name || `Team ${entry.roster_id}`,
    avatar: user.avatar || null,
    rank: rankByRoster[entry.roster_id] ?? null,
    points: entry.points ?? 0,           // raw float; renderer rounds for board
    record: `${roster.wins ?? 0}-${roster.losses ?? 0}${roster.ties ? '-' + roster.ties : ''}`,
    wins: roster.wins ?? 0,
    losses: roster.losses ?? 0,
  };
}

// Identity for poll history: the pair of roster_ids in a game. Must stay in
// sync with useBoardFeed.historyKey -- they are two ends of the same map.
export function historyKeyFor(matchup) {
  const a = matchup.teamA?.rosterId;
  const b = matchup.teamB?.rosterId;
  if (a == null && b == null) return `m${matchup.matchupId}`;
  const x = Number(a ?? -1);
  const y = Number(b ?? -1);
  return x < y ? `p${x}:${y}` : `p${y}:${x}`;
}

// ---- status state machine (Tier 1: week-close proxy) ----------------------
//
// deriveStatus takes everything Tier 2 would need, so the upgrade path is
// local to this function:
//   matchup  - { teamA, teamB } this week's entries (points live here)
//   prev     - array of prior snapshots for THIS matchup, oldest->newest,
//              each { points: [a,b], t: epochMs }. Tier 1 ignores it.
//   nflState - /state/nfl result (week, season_type)
//   now      - Date (injectable for testing)
//   weekOfRows - the week these rows were fetched for
//
// Tier 1 states, all derivable from Sleeper alone:
//   projected  - week open, no points yet (baseline)
//   live       - points changed since last poll (needs prev; falls back to
//                in_progress when prev is unavailable)
//   in_progress- points > 0 but week not closed
//   final      - scoring week has closed (nflState rolled past weekOfRows,
//                or season_type is 'post'/'off')  <-- the week-close proxy
export function deriveStatus(matchup, prev, nflState, now = new Date(), weekOfRows = null) {
  const a = matchup.teamA?.points ?? 0;
  const b = matchup.teamB?.points ?? 0;
  const anyPoints = a > 0 || b > 0;

  const seasonType = nflState?.season_type;
  const liveWeek = nflState?.week ?? 0;
  const wk = weekOfRows ?? liveWeek;

  // --- week-close proxy for FINAL ---
  // If we're viewing a week that the league has already rolled past, or the
  // season is over, the week is closed => Final. (For last season's data with
  // state.week=0/off, every historical week reads Final, which is correct.)
  const weekClosed =
    seasonType === 'off' ||
    seasonType === 'post' ||
    (typeof wk === 'number' && liveWeek > wk);
  if (weekClosed) return 'final';

  if (!anyPoints) return 'projected';

  // --- live: did points move since the previous poll? (Tier 1 uses prev only
  // for the live/in_progress distinction; absence of prev is fine.) ---
  if (prev && prev.length) {
    const last = prev[prev.length - 1];
    const [pa, pb] = last.points || [];
    if ((pa != null && pa !== a) || (pb != null && pb !== b)) return 'live';
  }

  return 'in_progress';
}

// Board shows three fantasy statuses, each with its own flip-dot color:
//   PREGAME  (yellow) — week open, no scores yet
//   ACTIVE   (green)  — points are on the board, week still open
//   FINAL    (white)  — scoring week has closed
// The richer internal states collapse: live + in_progress -> active.
export function statusToBoard(status) {
  if (status === 'final') return 'final';
  if (status === 'projected') return 'project';
  return 'active'; // live, in_progress
}

// ---- top-level: raw endpoints -> render-ready board -----------------------

export function buildBoard({ league, users, rosters, matchups, week, bracket }, opts = {}) {
  const { nflState = null, prevByMatchup = {}, now = new Date() } = opts;

  const usersById = buildUsersById(users);
  const rosterById = buildRosterById(rosters);
  const rankByRoster = computeRanks(rosterById);

  // group matchup entries by shared matchup_id
  const groups = {};
  for (const entry of matchups) {
    (groups[entry.matchup_id] ||= []).push(entry);
  }

  const rows = Object.entries(groups).map(([matchupId, entries]) => {
    const [ea, eb] = entries;
    const teamA = decorateTeam(ea, rosterById, usersById, rankByRoster);
    const teamB = eb ? decorateTeam(eb, rosterById, usersById, rankByRoster) : null;
    const matchup = { matchupId: Number(matchupId), teamA, teamB };
    // Poll history is keyed by roster PAIR, not matchup id: playoff
    // annotation hasn't run yet here, and a bracket game's matchup_id is
    // not guaranteed stable across rounds. The two rosters are.
    // Falls back to the old matchup-id key so callers passing legacy
    // history (or none) keep working. See useBoardFeed.historyKey.
    const hist =
      prevByMatchup[historyKeyFor(matchup)] ?? prevByMatchup[matchupId];
    const status = deriveStatus(matchup, hist, nflState, now, week);
    return { ...matchup, status, boardStatus: statusToBoard(status) };
  });

  // Order rows by the higher-ranked team so the board reads top-down, then by
  // matchup_id as a stable tiebreak.
  //
  // WHY THE TIEBREAK MATTERS: row order is what binds a physical board line to
  // a matchup. If two rows tie on best-rank and sort returns them in a
  // different order next poll, every cell in both rows re-targets and the
  // NAMES flip -- team names must never flip mid-week. matchup_id is fixed for
  // the week, so this makes row order deterministic.
  //
  // `rowOrder: 'fixed'` pins rows to matchup_id outright, so a mid-week rank
  // change can't reorder the board at all. That's the calmer default for an
  // ambient display; 'rank' keeps the old rank-led ordering.
  const rowOrder = opts.rowOrder || 'rank';
  rows.sort((x, y) => {
    if (rowOrder === 'fixed') return x.matchupId - y.matchupId;
    const rx = Math.min(x.teamA?.rank ?? 99, x.teamB?.rank ?? 99);
    const ry = Math.min(y.teamA?.rank ?? 99, y.teamB?.rank ?? 99);
    return (rx - ry) || (x.matchupId - y.matchupId);
  });

  // ---- playoff layer -------------------------------------------------------
  // Purely additive. Outside the playoffs, or with no bracket, this is the
  // identity function and every row keeps the shape it has always had.
  //
  // Inside the playoffs it does three things the regular season doesn't need:
  // drops consolation games (which /matchups returns for every roster),
  // labels each surviving row with its round, and re-sorts so the title game
  // is the top line. See playoffs.js for why the label is per-MATCH and not
  // per-round.
  const { rows: finalRows, playoff } = annotatePlayoffRows(
    rows, bracket, week, league,
    { dropConsolation: opts.dropConsolation !== false }
  );

  const eliminated = new Set(playoff?.eliminated || []);
  const inBracket = new Set(playoff?.inBracket || []);

  // standings view (§2): same data, flat list sorted by rank.
  // In the playoffs each team also carries its bracket state, so the
  // standings view can dim teams that are out without changing its ordering
  // (season standings are season standings -- elimination doesn't reorder
  // them, it just annotates them).
  const standings = Object.values(rosterById)
    .map((r) => {
      const u = usersById[r.ownerId] || {};
      const row = {
        rank: rankByRoster[r.rosterId],
        rosterId: r.rosterId,
        name: u.name || `Team ${r.rosterId}`,
        avatar: u.avatar || null,
        record: `${r.wins}-${r.losses}${r.ties ? '-' + r.ties : ''}`,
        pointsFor: r.fpts,
      };
      if (playoff) {
        row.eliminated = eliminated.has(r.rosterId);
        row.inPlayoffs = inBracket.has(r.rosterId);
        row.isChampion = playoff.championRosterId === r.rosterId;
      }
      return row;
    })
    .sort((a, b) => a.rank - b.rank);

  // Finishing medals (§13). Placements come from the BRACKET, not from
  // regular-season rank -- the point of a playoff is that seeding doesn't
  // decide it.
  //
  // ALL markers gate on the title game being FINAL, including last place.
  // Last place is otherwise just "bottom of the table", which is true from
  // the moment the playoffs start -- and shipping a poop emoji to whoever
  // is last in week 15, three weeks before the season ends, is both wrong
  // and needlessly rude. `championDecided` is the single gate.
  const seasonOver = championDecided(bracket);
  const places = seasonOver ? finalPlacements(bracket) : { first: null, second: null, third: null };
  const lastId = seasonOver ? lastPlaceRosterId(standings) : null;
  for (const s of standings) {
    if (!playoff) continue;
    s.place =
      s.rosterId === places.first ? 1 :
      s.rosterId === places.second ? 2 :
      s.rosterId === places.third ? 3 : null;
    s.isLastPlace = lastId != null && s.rosterId === lastId;
  }

  return {
    leagueName: league?.name?.trim() || 'FANTASY',
    week,
    rows: finalRows,
    standings,
    // null outside the playoffs; the header and rows both branch on this
    playoff,
    playoffWeekStart: playoffWeekStart(league),
  };
}

// Resolve which week to actually display for a league.
//
// THE OFF-SEASON PROBLEM: Sleeper's /state/nfl returns week:0 between
// seasons, so the obvious fallback (display_week, which is 1) asks a league
// for week 1 of a season it hasn't played yet -> zero matchups -> an empty
// board, even though the league's full standings are sitting right there.
//
// A finished league should show its LAST PLAYED week, not a week that
// doesn't exist. We walk backwards from the league's own settings to find
// the most recent week that actually returns matchups. Capped at 15 per §4
// (week never exceeds 15) and bounded to a handful of probes so a dead
// league can't turn one page load into eighteen API calls.
// THE CONSOLATION-WEEK PROBLEM (new): the backwards walk stops at the first
// week that has ANY matchups. During and after the playoffs that can be a
// week whose only games are consolation -- so a finished league's "FINAL"
// board shows the 7th-place game while the trophy sits three rows away in
// standings. A finished league's final state is the week the TITLE was
// decided, so we ask the bracket first and only fall back to the walk.
export async function resolveDisplayWeek(leagueId, nflState, fetchImpl = fetch, opts = {}) {
  const { maxProbes = 6 } = opts;

  // In season: trust the live week.
  if (nflState?.week > 0) return { week: nflState.week, inSeason: true };

  // Off-season: the championship week beats any probing, when it exists and
  // was actually played. An unplayed bracket (league scheduled playoffs but
  // folded) falls through to the walk, which is the honest answer there.
  let bracket = [];
  try {
    const [league, b] = await Promise.all([
      getJSON(`${BASE}/league/${leagueId}`, fetchImpl),
      fetchBracket(leagueId, fetchImpl, BASE),
    ]);
    bracket = b;
    if (championDecided(bracket)) {
      const cw = championshipWeek(bracket, league);
      if (cw) return { week: cw, inSeason: false, bracket, isChampionshipWeek: true };
    }
  } catch {
    // No league or no bracket -- fall through to the walk below.
  }

  // Fallback: start from where the league finished and walk back to the last
  // week that actually returned matchups.
  const start = Math.min(18, Number(nflState?.display_week) || 1);
  const from = Math.max(start, 17);

  for (let w = from, probes = 0; w >= 1 && probes < maxProbes; w--, probes++) {
    try {
      const m = await getJSON(`${BASE}/league/${leagueId}/matchups/${w}`, fetchImpl);
      // A played week has matchups with points on them.
      if (Array.isArray(m) && m.length > 0) {
        return { week: w, inSeason: false, bracket };
      }
    } catch {
      // a 404 on one week is not fatal; keep walking back
    }
  }
  return { week: start, inSeason: false, bracket };
}

// Convenience: fetch + build in one call (in-season path).
export async function loadBoard(leagueId, fetchImpl = fetch) {
  const nflState = await fetchNflState(fetchImpl);
  const { week, bracket } = await resolveDisplayWeek(leagueId, nflState, fetchImpl);
  // Reuse the bracket resolveDisplayWeek already fetched rather than paying
  // for it twice.
  const raw = await fetchLeague(leagueId, week, fetchImpl, { bracket });
  return buildBoard(raw, { nflState });
}
